import { app, v } from "modular-openscriptjs";
import { IdGenerator, Pipeline, Requester } from "./fotastart";
import { routes as backendRoutes } from "./backendRoutes";
import { GoogleAnalytics } from "./analytics";
import {
	authenticated,
	can,
	formatThousand,
	redirect,
} from "./helpers";

// Import Classes from their new home
import { Notice, NoticeWrapper } from "./classes/Notice";
import { NavItem } from "./classes/NavItem";
import { Middleware } from "./classes/Middleware";
import { MainActionData } from "./classes/MainActionData";

// Import Contexts
import { gc, uc, dc, pc } from "./ojs.contexts";

// resolve the current page
app().value("currentPageResolver", () => null);

export const resolveCurrentPage = () => {
	return app("currentPageResolver")();
}

/**
 * @param {() => HTMLElement} callback
 */
export const resolveCurrentPageUsing = (callback) => {
	app().value("currentPageResolver", callback);
}

export { Notice, NoticeWrapper, NavItem, Middleware, MainActionData, gc, uc, dc, pc };

// Network Requests
export const api = new Requester();

// ID generator
export const _UNIQUE_ID_ = new IdGenerator();

export const STRINGS = {
	SUCCESS: "success",
	ERROR: "error",
	INFO: "info",
	WARNING: "warning",
	DEFAULT: "default",
	CAPA_SYMBOL: "Ç",
};

/**
 * All pages
 * @enum {string}
 */
export const PAGES = {
	HOME: "Home",
	PROFILE: "Profile",
	EDIT_PROFILE: "Edit Profile",
	CHANGE_PASSWORD: "Change Password",
	FORGOT_PASSWORD: "Forgot Password",
	RESET_PASSWORD: "Reset Password",
	PAYMENT: "Make Payment",
	CONFIRM_PAYMENT: "Confirm Payment",
	ORDER_DETAILS: "Order Details",
	SIGNUP: "Sign up",
	LOGIN: "Login",
	FAQS: "Faqs",
	SETTINGS: "Settings",
	WALLET: "My Wallet",
	AWARDS: "Awards",
	VIEW_PRODUCT: "View Product",
	BUSINESSES: "Businesses",
	TRANSFERS: "Capa Transfers",
	PAGE_404: "Not Found",
	OTP: "Identity",
	HASHTAGS_LOCATION: "Hashtags Locations",
	PROCESS_ORDERS: "Process Orders",
	REVIEWS: "Reviews",
	BUSINESSES: "Businesses",
};

export const ACTION_CALLBACKS = {};

const $e = app("$e");
const h = app("h");

/**
 * Default response pipeline
 */
export const rPipeline = new Pipeline();

export const analytics = new GoogleAnalytics();

gc.root = h.dom.querySelector("#app-root");

// Declare initial navItems

export let bottomNavItems = [
	NavItem.new({
		icon: () => h.i({ class: "bi bi-house" }),
		route: `/`,
		text: PAGES.HOME,
		appHook: 'home',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-clipboard-check" }),
		route: `/orders`,
		pageName: PAGES.PROCESS_ORDERS,
		text: "Orders",
		appHook: 'orders',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-star" }),
		route: '/reviews',
		text: "Reviews",
		appHook: 'reviews',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-three-dots" }),
		onclick: `app("sendEvent")('${$e.ui.needs.offcanvas.nav}')`,
		text: "More",
		appHook: 'more-options',
	}),
];

export let offcanvasNavItems = [
	NavItem.new({
		icon: () => h.i({ class: "bi bi-award" }),
		route: 'user.awards',
		text: "Awards",
		appHook: 'awards',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-wallet" }),
		route: `/wallet`,
		text: PAGES.WALLET,
		appHook: 'wallet',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-geo-alt" }),
		route: `system.hashtag-locations`,
		condition: () => can("add-hashtag-location"),
		text: PAGES.HASHTAGS_LOCATION,
		appHook: 'hashtag-locations',
	}),

	NavItem.new({
		text: PAGES.PROFILE,
		onclick: h.func("app('router').to", "user.view-profile", {
			user: "current",
		}),
		icon: () => h.i({ class: "bi bi-person" }),
		appHook: 'profile',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-arrow-left-right" }),
		route: "/companies/select",
		text: "Switch Company",
		appHook: 'switch-company',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-gear" }),
		route: `/settings`,
		text: PAGES.SETTINGS,
		appHook: 'settings',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-power" }),
		onclick: `app("sendEvent")('${$e.user.needs.logout}')`,
		text: "Logout",
		appHook: 'logout',
	}),

	NavItem.new({
		icon: () => h.i({ class: "fa-solid fa-power-off" }),
		onclick: `app("sendEvent")('${$e.user.needs.authentication}')`,
		text: "Login",
		condition: () => !authenticated(),
		appHook: 'login',
	}),
];

export let sideNavItems = [
	NavItem.new({
		icon: () => h.i({ class: "bi bi-house-door" }),
		route: `/`,
		text: PAGES.HOME,
		appHook: 'home',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-clipboard-check" }),
		route: `/orders`,
		pageName: PAGES.PROCESS_ORDERS,
		text: "Orders",
		appHook: 'orders',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-star" }),
		route: '/',
		text: "Reviews",
		appHook: 'reviews',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-award" }),
		route: `/awards`,
		text: PAGES.AWARDS,
		appHook: 'awards',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-wallet" }),
		route: `/wallet`,
		text: PAGES.WALLET,
		appHook: 'wallet',
	}),

	NavItem.new({
		text: PAGES.PROFILE,
		onclick: h.func(`app("router").to`, "user.view-profile", {
			user: "current",
		}),
		icon: () => h.i({ class: "bi bi-person" }),
		appHook: 'profile',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-geo-alt" }),
		route: `system.hashtag-locations`,
		condition: () => can("add-hashtag-location"),
		text: PAGES.HASHTAGS_LOCATION,
		appHook: 'hashtag-locations',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-gear" }),
		route: `/settings`,
		text: PAGES.SETTINGS,
		appHook: 'settings',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-power" }),
		onclick: `app("sendEvent")('${$e.user.needs.logout}')`,
		text: "Logout",
		appHook: 'logout',
	}),
];

export const routes = backendRoutes;